import React, { FC, MouseEvent, useCallback, useMemo } from 'react';

import { CellContext } from '@tanstack/react-table';

import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import { track } from '@adsk/acs-web-analytics';
import { EntitlementsProject } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import { navigate } from '@adsk/exo-skeleton-api';

import { ProductsListContainer } from './ProductsListContainer';
import { AnalyticsEvents } from '../../../constants';

export const QuickAccessProductsCell: FC<
  CellContext<EntitlementsProject, EntitlementsProject['products']>
> = ({ row }) => {
  const disableRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);
  const project = row.original;
  const currentProduct = useMemo(
    () =>
      project.products?.find(
        (product) => product.defaultUrl === project.defaultUrl,
      ) || project.products[0],
    [project.defaultUrl, project.products],
  );

  const onProductClick = useCallback(
    async (event: MouseEvent, { url, id }) => {
      track(AnalyticsEvents.productEntryFlowNavigate, {
        project_name: project.name,
        product_id: id,
      });

      if (
        event.ctrlKey ||
        event.metaKey ||
        event.button === 1 || // support middle mouse button
        !url.startsWith(getAccHostname())
      ) {
        // check if the user is holding down the Ctrl or Command key (temp solution until we have ExoLink component)
        window.open(url, '_blank');
      } else {
        await navigate(url);
      }
    },
    [project.name],
  );

  if (row.original.platform !== 'acc' || !currentProduct) {
    return null;
  }

  return (
    <Flex
      height="100%"
      data-testid={`QuickAccess:${row.original.id}`}
      onClick={disableRowClick}
    >
      <ProductsListContainer
        isLink={true}
        project={row.original}
        currentProduct={currentProduct}
        onProductClick={onProductClick}
      />
    </Flex>
  );
};
